import * as Sentry from '@sentry/react';

export function initSentry(): boolean {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENVIRONMENT || 'development';

  // Allow disabling Sentry
  if (import.meta.env.VITE_SENTRY_DISABLED === 'true') {
    console.info('Sentry integration disabled via VITE_SENTRY_DISABLED');
    return false;
  }

  if (!sentryDsn) {
    console.warn('VITE_SENTRY_DSN not set, Sentry integration disabled');
    return false;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 0,
    replaysOnErrorSampleRate: environment === 'production' ? 0.5 : 1.0,
    beforeSend(event, hint) {
      const error = hint?.originalException;

      if (error && typeof error === 'object') {
        const status =
          (error as { status?: number }).status ??
          (error as { response?: { status?: number } }).response?.status;
        // Drop expected client errors (auth / not-found); keep actionable
        // 4xx like 400/409/422 and all 5xx.
        if (status === 401 || status === 403 || status === 404) {
          return null;
        }
      }

      return event;
    },
  });

  console.info(`Sentry initialized for environment: ${environment}`);
  return true;
}

export function captureError(
  error: Error,
  context?: Record<string, unknown>
): string | undefined {
  return Sentry.captureException(error, {
    extra: context,
  });
}

export function setUser(user: { id: string; email?: string; role?: string }): void {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    role: user.role,
  });
}

export function clearUser(): void {
  Sentry.setUser(null);
}

export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level: 'info',
  });
}
